import type {
  CtProductSearchSortCriterion,
  CtSearchFilterDefinition,
  GetCtProductSearchSortParams,
} from '~~/types/commercetools/productSearch';

// https://docs.commercetools.com/api/projects/product-search
export const CT_SEARCH_FIELD = {
  CATEGORIES_SUB_TREE: 'categoriesSubTree',
  SKU: 'variants.sku',
  BRAND: 'variants.attributes.brand',
  TAGS: 'variants.attributes.tags',
  ELIGIBLE_FOR_SELLING: 'variants.attributes.eligibleForSelling',
  SELLABLE_ON_MARKET: 'variants.attributes.sellable_on_store',
  // currentCentAmount includes discounts, matching the price shown on the card; centAmount doesn't.
  PRICE: 'variants.prices.currentCentAmount',
  PRICE_CURRENCY_CODE: 'variants.prices.currencyCode',
  PRICE_COUNTRY: 'variants.prices.country',
  PRICE_IS_DISCOUNTED: 'variants.prices.discounted',
  IS_ON_STOCK: 'variants.availability.isOnStock',
  IS_BWARE: 'variants.attributes.isBware',
  AVG_RATING: 'reviewRatingStatistics.averageRating',
  REVIEWS_COUNT: 'reviewRatingStatistics.count',
  CREATED_AT: 'createdAt',
} as const;

// CT rejects a request whose offset + limit goes past this.
export const CT_SEARCH_MAX_RESULT_WINDOW = 10000;

export const CT_SEARCH_MAX_FACET_BUCKETS = 200;

export const PRODUCT_LISTING_REFINEMENT_OPERATOR = {
  GTE: '>=',
  LTE: '<=',
} as const;

export type ProductListingRefinementOperator =
  typeof PRODUCT_LISTING_REFINEMENT_OPERATOR[keyof typeof PRODUCT_LISTING_REFINEMENT_OPERATOR];

// Same symbols InstantSearch uses in numeric current-refinement labels.
export const PRODUCT_LISTING_REFINEMENT_OPERATOR_SYMBOL: Record<ProductListingRefinementOperator, string> = {
  [PRODUCT_LISTING_REFINEMENT_OPERATOR.GTE]: '≥',
  [PRODUCT_LISTING_REFINEMENT_OPERATOR.LTE]: '≤',
};

export const CT_SEARCH_FACET_NAME = {
  BRAND: 'brand',
  TAGS: 'tags',
  PRICE: 'price',
  AVG_RATING: 'avgRating',
  CATEGORY: 'category',
} as const;

export const CT_SEARCH_FILTER_KIND = {
  REFINEMENT_LIST: 'refinementList',
  PRICE: 'price',
  RATING: 'rating',
} as const;

// The listing filters in production sidebar order. The server builds the facets and filter nodes from
// this list, the client the URL state, the sidebar and the active-filter chips.
export const CT_SEARCH_FILTERS = [
  {
    kind: CT_SEARCH_FILTER_KIND.REFINEMENT_LIST,
    name: CT_SEARCH_FACET_NAME.TAGS,
    field: CT_SEARCH_FIELD.TAGS,
    fieldType: 'ltext',
    titleKey: 'Tags',
    hideTitle: true,
    isJsonArray: true,
  },
  {
    kind: CT_SEARCH_FILTER_KIND.PRICE,
    name: CT_SEARCH_FACET_NAME.PRICE,
    field: CT_SEARCH_FIELD.PRICE,
    titleKey: 'Price',
  },
  {
    kind: CT_SEARCH_FILTER_KIND.RATING,
    name: CT_SEARCH_FACET_NAME.AVG_RATING,
    field: CT_SEARCH_FIELD.AVG_RATING,
    titleKey: 'Ratings',
  },
  {
    kind: CT_SEARCH_FILTER_KIND.REFINEMENT_LIST,
    name: CT_SEARCH_FACET_NAME.BRAND,
    field: CT_SEARCH_FIELD.BRAND,
    fieldType: 'ltext',
    titleKey: 'brand',
  },
] as const satisfies readonly CtSearchFilterDefinition[];

export type CtSearchFilter = typeof CT_SEARCH_FILTERS[number];
export type CtSearchFilterName = CtSearchFilter['name'];
type CtSearchRefinementListFilter = Extract<CtSearchFilter, { kind: typeof CT_SEARCH_FILTER_KIND.REFINEMENT_LIST }>;
export type CtSearchRefinementListFilterName = CtSearchRefinementListFilter['name'];

export const CT_SEARCH_REFINEMENT_LIST_FILTER_NAMES = CT_SEARCH_FILTERS
  .filter((filter): filter is CtSearchRefinementListFilter => filter.kind === CT_SEARCH_FILTER_KIND.REFINEMENT_LIST)
  .map(filter => filter.name);

// Maps SORTING_METHODS (constants/typesense.ts) to CT sorts. The language and the price scope are
// only known per request, hence a function.
export const getCtProductSearchSort = (
  { sortKey, language, priceScopeFilter }: GetCtProductSearchSortParams,
): CtProductSearchSortCriterion[] => {

  const sortMap: Record<string, CtProductSearchSortCriterion[]> = {
    // Typesense's default ranking. isBware is a custom attribute, so CT needs its `fieldType`.
    '': [
      { field: CT_SEARCH_FIELD.IS_ON_STOCK, order: 'desc' },
      { field: CT_SEARCH_FIELD.IS_BWARE, order: 'asc', fieldType: 'boolean' },
      { field: CT_SEARCH_FIELD.PRICE_IS_DISCOUNTED, order: 'desc', filter: priceScopeFilter },
      { field: CT_SEARCH_FIELD.REVIEWS_COUNT, order: 'desc' },
      { field: CT_SEARCH_FIELD.AVG_RATING, order: 'desc' },
    ],
    nameAsc: [{ field: 'name', order: 'asc', language }],
    nameDesc: [{ field: 'name', order: 'desc', language }],
    priceAsc: [{ field: CT_SEARCH_FIELD.PRICE, order: 'asc', mode: 'min', filter: priceScopeFilter }],
    priceDesc: [{ field: CT_SEARCH_FIELD.PRICE, order: 'desc', mode: 'max', filter: priceScopeFilter }],
    newest: [
      { field: CT_SEARCH_FIELD.IS_ON_STOCK, order: 'desc' },
      { field: CT_SEARCH_FIELD.CREATED_AT, order: 'desc' },
    ],
  };

  return sortMap[sortKey] ?? sortMap[''] ?? [];

};
